import { css, styled } from "@pigment-css/react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { ResponsiveTable } from "@acto/taskpane/core/components/markdown";

const Headline1 = styled("p")({
  fontSize: "21px",
  lineHeight: "120%",
  fontWeight: 640,
  marginBottom: "24px",
  color: "rgba(0, 0, 0, 0.94)",
});

const Headline2 = styled("p")({
  fontSize: "18px",
  lineHeight: "120%",
  letterSpacing: "-1%",
  fontWeight: 580,
  marginBottom: "8px",
  marginTop: "16px",
  color: "rgba(0, 0, 0, 0.94)",
});

const Headline3 = styled("p")({
  fontSize: "16px",
  lineHeight: "140%",
  fontWeight: 580,
  marginBottom: "4px",
  marginTop: "12px",
  color: "rgba(0, 0, 0, 0.94)",
});

const Text = styled("p")({
  fontSize: "16px",
  lineHeight: "140%",
  fontWeight: 440,
  letterSpacing: "-1.75%",
  wordBreak: "break-word",
  marginBottom: "8px",
  color: "rgba(0, 0, 0, 0.84)",
});

const listStyles = css({
  margin: "0 0 16px 0",
  paddingLeft: "8px",
  listStyleType: "disc",
  "ol&": { listStyleType: "decimal" },
  "& *": {
    fontSize: "16px",
    lineHeight: "150%",
    fontWeight: 440,
    letterSpacing: "-1.75%",
  },
  "& li > span > ul": { marginTop: "8px", marginBottom: 0, paddingLeft: "10px" },
});

const HorizontalRule = styled("div")(({ theme }) => ({
  width: "100%",
  height: 1,
  backgroundColor: theme.colors.transparent[100],
  marginTop: "24px",
  marginBottom: "24px",
}));

const Link = styled("a")(({ theme }) => ({
  color: theme.colors.brand[500],
  textDecoration: "none",
  fontWeight: 520,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  "&:hover": { color: theme.colors.brand[700], textDecoration: "underline" },
}));

const mdComponents = {
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Headline1 {...props} />,
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Headline2 as="h2" {...props} />,
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Headline3 as="h3" {...props} />,
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  strong: (props: React.HTMLAttributes<HTMLElement>) => <strong style={{ fontWeight: "600" }} {...props} />,
  em: (props: React.HTMLAttributes<HTMLElement>) => <em style={{ fontStyle: "italic" }} {...props} />,
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <Link {...props} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => <ul className={listStyles} {...props} />,
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => <ol className={listStyles} {...props} />,
  li: (props: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li style={{ marginBottom: "8px", marginLeft: "6px" }}>
      <Text as="span" {...props} />
    </li>
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => <HorizontalRule {...props} />,
  table: (props: React.HTMLAttributes<HTMLTableElement>) => <ResponsiveTable {...props} />,
};

interface MarkdownRendererProps {
  content: string;
}

export const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  return (
    <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} components={mdComponents}>
      {content}
    </ReactMarkdown>
  );
};
